import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import {
    ActionItemSuggestionsResponse,
    ActionItemFilterState,
    defaultFilterState,
} from './types'

interface AcceptSuggestionsPayload {
    suggestion_ids: number[]
    service_id: number | null
    vendor_id: number | null
    assigned_user_id: number | null
}

interface RejectSuggestionsPayload {
    suggestion_ids: number[]
}

export const useActionItemSuggestions = (inspectionId: number | undefined) => {
    const queryClient = useQueryClient()

    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(25)
    const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc')
    const [filters, setFilters] = useState<ActionItemFilterState>(
        defaultFilterState,
    )

    const queryKey = [
        'actionItemSuggestions',
        inspectionId,
        page,
        pageSize,
        sortDirection,
        filters,
    ]

    const { data, isLoading, error } = useQuery<
        AxiosResponse<ActionItemSuggestionsResponse>,
        Error
    >({
        queryKey,
        queryFn: () => {
            const params = new URLSearchParams()
            params.append('page', page.toString())
            params.append('page_size', pageSize.toString())
            params.append('sort_direction', sortDirection)

            if (filters.statuses.length) {
                params.append('statuses', filters.statuses.join(','))
            }
            if (filters.flagFilter !== 'no_filter') {
                const flagValue =
                    filters.flagFilter === 'has_flag'
                        ? 'flagged'
                        : 'not_flagged'
                params.append('flag_filter', flagValue)
            }
            if (
                filters.confidenceOperator !==
                    defaultFilterState.confidenceOperator ||
                filters.confidenceThreshold !==
                    defaultFilterState.confidenceThreshold
            ) {
                params.append('confidence_operator', filters.confidenceOperator)
                params.append(
                    'confidence_threshold',
                    filters.confidenceThreshold.toString(),
                )
            }
            if (filters.services.length) {
                params.append('service_ids', filters.services.join(','))
            }
            if (filters.vendors.length) {
                params.append('vendor_ids', filters.vendors.join(','))
            }
            if (filters.unitIds.length) {
                params.append('unit_ids', filters.unitIds.join(','))
            }

            return axiosInstance.get(
                `/workorder/${inspectionId}/action-item-suggestions/?${params.toString()}`,
            )
        },
        enabled: !!inspectionId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const acceptSuggestions = useMutation({
        mutationFn: (payload: AcceptSuggestionsPayload) =>
            axiosInstance.post(
                `/workorder/${inspectionId}/action-item-suggestions/accept/`,
                payload,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['actionItemSuggestions', inspectionId],
            })
            toast.success('Suggestions accepted')
        },
        onError: () => {
            toast.error('Failed to accept suggestions')
        },
    })

    const rejectSuggestions = useMutation({
        mutationFn: (payload: RejectSuggestionsPayload) =>
            axiosInstance.post(
                `/workorder/${inspectionId}/action-item-suggestions/reject/`,
                payload,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['actionItemSuggestions', inspectionId],
            })
            toast.success('Suggestions rejected')
        },
        onError: () => {
            toast.error('Failed to reject suggestions')
        },
    })

    const responseData = data?.data ?? null

    return {
        data: responseData,
        suggestions: responseData?.suggestions ?? [],
        summary: responseData?.summary ?? null,
        bulkActions: responseData?.bulk_actions ?? null,
        matchingSuggestionIds:
            responseData?.bulk_actions?.matching_suggestion_ids ?? [],
        inspectionName: responseData?.inspection_name ?? '',
        inspectionType: responseData?.inspection_type ?? '',
        isLoading,
        error,
        page,
        setPage,
        pageSize,
        setPageSize,
        sortDirection,
        setSortDirection,
        filters,
        setFilters,
        acceptSuggestions,
        rejectSuggestions,
    }
}
